import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MITPService } from "./mitp.service";
import { AutocompleteDrug, Drug, DrugDetails, EDrugIcon } from "../entities/drug";
import { EMPTY, first, Observable, of, switchMap } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { DrugDetailsComponent } from "../modules/pharmacy/drug-details/drug-details.component";
import { NotificationService } from "./notification.service";
import { DrugInstitute } from "../entities/pharmacy/drug-institute";
import { catchError, map } from "rxjs/operators";
import { TranslateService } from "@tolgee/ngx";
import { EDrugIndicators } from "../enums/edrug-indicators";
import { IDrugFilterIndicator, IDrugIndicator, TDrugIndicatorKey } from "../interfaces/pharmacy/idrug-indicator";

/** Fichier image illustrant chaque forme galénique. */
const DRUG_ICON_FILES: Record<EDrugIcon, string> = {
	[EDrugIcon.TABLET]: "pill",
	[EDrugIcon.DROP]: "drop",
	[EDrugIcon.SACHET]: "sachet",
	[EDrugIcon.NEEDLE]: "needle",
	[EDrugIcon.PAD]: "pad",
	[EDrugIcon.PELLET]: "pellet",
	[EDrugIcon.PATCH]: "patch",
	[EDrugIcon.BOTTLE]: "bottle",
	[EDrugIcon.CREAM]: "cream",
	[EDrugIcon.IUD]: "iud",
	[EDrugIcon.SUPPOSITORY]: "suppository",
};

@Injectable({
	providedIn: "root",
})
export class DrugsService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);
	private dialog = inject(MatDialog);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);

	search(keyword: string, booklet: boolean = true): Observable<AutocompleteDrug[]> {
		return this.httpClient.get<AutocompleteDrug[]>(
			this.mitp.Drugs() + `/search?keyword=${keyword}&booklet=${booklet}`
		);
	}

	getDrugByUcd(ucd: string): Observable<Drug> {
		return this.httpClient.get<Drug>(this.mitp.Drugs() + `/ucd/${ucd}`);
	}

	getDrugInstituteByUcd(ucd: string): Observable<DrugInstitute> {
		return this.httpClient.get<DrugInstitute>(this.mitp.Drugs() + `/ucd/${ucd}/institutes`);
	}

	getContraindications(vmpId: string): Observable<{ contraindications: string[] }> {
		return this.httpClient.get<{ contraindications: string[] }>(
			this.mitp.Drugs() + `/contraindications/vmp/${vmpId}`
		);
	}

	getDrugDetails(ucd: string): Observable<DrugDetails> {
		return this.httpClient.get<DrugDetails>(this.mitp.Drugs() + `/ucd/${ucd}/details`);
	}

	checkShortage(ucds: string[]): Observable<string[]> {
		return this.httpClient.post<string[]>(this.mitp.Drugs() + `/shortage/ucds`, { ucds: ucds });
	}

	showDrug(ucd: string | DrugInstitute): void {
		if (typeof ucd === "string") {
			this.getDrugInstituteByUcd(ucd)
				.pipe(
					first(),
					switchMap((drugInstitute) => {
						if (drugInstitute) {
							return of(drugInstitute);
						} else {
							return this.getDrugByUcd(ucd).pipe(
								first(),
								map(
									(drug) =>
										({
											alternatives: [],
											drug: drug,
											id: -1,
											institute: undefined,
											lastUpdated: undefined,
											reference: "",
											risk: false,
											stock: null,
										}) as DrugInstitute
								)
							);
						}
					}),
					catchError(() => {
						this.toastr.error(this.translate.instant("drugs.drug.search.error"));
						return EMPTY;
					})
				)
				.subscribe((drugInstitute) => this.openDrugDetails(drugInstitute));
		} else {
			this.openDrugDetails(ucd);
		}
	}

	openDrugDetails(drugInstitute: DrugInstitute): void {
		this.dialog.open(DrugDetailsComponent, {
			data: drugInstitute,
			width: "600px",
			autoFocus: false,
		});
	}

	/**
	 * Pastilles filtrables côté API. Source unique de vérité pour les puces de
	 * filtre, la légende et les badges de ligne : une couleur signifie la même
	 * chose partout dans l'application.
	 */
	readonly filterIndicators: IDrugFilterIndicator[] = [
		{
			key: "shortage",
			icon: "block",
			label: "drugs.drug.indicators.shortage.label",
			badgeClasses: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300",
			chipClasses:
				"border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
		},
		{
			key: "supplyTension",
			icon: "warning",
			label: "drugs.drug.indicators.tension.label",
			badgeClasses: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
			chipClasses:
				"border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
		},
		{
			key: "provisioning",
			icon: "inventory_2",
			label: "drugs.drug.indicators.provisioning.label",
			badgeClasses: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
			chipClasses:
				"border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
		},
		{
			key: "risk",
			icon: "report",
			label: "drugs.drug.risk",
			badgeClasses: "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300",
			chipClasses:
				"border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900 dark:bg-purple-950/40 dark:text-purple-300",
		},
	];

	/** Légende complète : les filtres, plus les alternatives qui ne sont pas filtrables. */
	readonly indicators: IDrugIndicator[] = [
		...this.filterIndicators,
		{
			key: "alternatives",
			icon: "swap_horiz",
			label: "drugs.drug.alternatives.label",
			badgeClasses: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
			chipClasses:
				"border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300",
		},
	];

	hasIndicator(item: DrugInstitute, key: TDrugIndicatorKey): boolean {
		switch (key) {
			case "risk":
				return item.risk;
			case "alternatives":
				return item.alternatives?.length > 0;
			case "shortage":
				return this.hasNationalIndicator(item, EDrugIndicators.INDICATOR_DRUG_SHORTAGE);
			case "supplyTension":
				return this.hasNationalIndicator(item, EDrugIndicators.INDICATOR_DRUG_SUPPLY_TENSION);
			case "provisioning":
				return this.hasNationalIndicator(item, EDrugIndicators.INDICATOR_DRUG_PROVISIONING);
		}
	}

	getIndicators(item: DrugInstitute): IDrugIndicator[] {
		return this.indicators.filter((indicator) => this.hasIndicator(item, indicator.key));
	}

	getIconPath(item: DrugInstitute): string {
		return `assets/images/${DRUG_ICON_FILES[item.drug?.icon] ?? "pill"}.png`;
	}

	private hasNationalIndicator(item: DrugInstitute, indicator: EDrugIndicators): boolean {
		return (item.drug?.indicators ?? []).includes(indicator.toString());
	}
}
